import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';

import { setDashboardFilter } from '@dashboards/actions/dashboard-filter.actions';
import { selectDashboardFilterState } from '@dashboards/selectors/dashboard-filter.selectors';
import {
  selectLateTurnoversCount,
  selectMyTasksLoading,
  selectMyTasksTicketsCount,
  selectMyTasksTurnoversCount,
  selectStaleTurnoversCount,
} from '@dashboards/selectors/dashboards.selectors';
import { selectActiveUsers } from '@main-application/administration/selectors/administration.selectors';
import { DashboardsAbstract } from '@main-application/dashboards/components/abstract/dashboards.abstract';
import { PriorityType } from '@shared/enums/priority-type';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { selectUserData } from '@shared/selectors/user.selectors';

@UntilDestroy()
@Component({
  selector: 'app-dashboard-user-header',
  templateUrl: './dashboard-user-header.component.html',
  styleUrls: ['./dashboard-user-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardUserHeaderComponent extends DashboardsAbstract implements OnInit, OnDestroy {
  myTasksTurnovers$: Observable<number>;
  staleTurnovers$: Observable<number>;
  lateTurnovers$: Observable<number>;
  myTasksTickets$: Observable<number>;
  myTasksLoading$: Observable<boolean>;
  assignee: UntypedFormControl = new UntypedFormControl();
  users: RestUserModel[];

  @Input() previousRoute?: string;
  @Input() headerValue = '';
  @Input() customHeaderTemplate: TemplateRef<any>;
  @Input() containerCss = '';
  @Input() showAssigneeControl = false;

  get showBack() {
    return this.previousRoute;
  }

  constructor(protected cdr: ChangeDetectorRef, private store: Store<{}>, private router: Router) {
    super(cdr);
  }

  ngOnInit(): void {
    combineLatest([this.store.select(selectUserData), this.store.select(selectDashboardFilterState)])
      .pipe(
        untilDestroyed(this),
        filter(([userData]) => !!userData),
        tap(([userData, filter]) => {
          this.userData = userData;
          if (filter?.assignee !== undefined) {
            this.assignee.setValue(filter.assignee ?? -1);
          } else {
            this.store.dispatch(setDashboardFilter({ assignee: this.userData.id }));
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.store
      .select(selectActiveUsers)
      .pipe(
        untilDestroyed(this),
        filter((users: RestUserModel[]) => !!users?.length),
        tap((users: RestUserModel[]) => {
          this.users = users;
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.assignee.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(assignee => {
          this.store.dispatch(setDashboardFilter({ assignee: assignee > 0 ? assignee : null }));
        })
      )
      .subscribe();

    this.myTasksLoading$ = this.store.select(selectMyTasksLoading);
    this.myTasksTurnovers$ = this.store.select(selectMyTasksTurnoversCount);
    this.myTasksTickets$ = this.store.select(selectMyTasksTicketsCount);
    this.staleTurnovers$ = this.store.select(selectStaleTurnoversCount);
    this.lateTurnovers$ = this.store.select(selectLateTurnoversCount);
  }

  goBack(evt: Event) {
    evt.preventDefault();
    this.router.navigateByUrl(this.previousRoute);
  }

  ngOnDestroy(): void {}

  protected readonly PriorityType = PriorityType;
}
